import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { MicrosoftSignIn } from "@/components/shared/MicrosoftSignIn";
import { PasswordSignIn } from "@/components/auth/PasswordSignIn";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const LOGO_URL = "/brand/jigsaw-logo-full.png";

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "password" ? "password" : "otp");
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await requestOtp(email);
      setDevOtp(data.dev_otp || null);
      setStep("otp");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goToWorkspace = async () => {
    const { data: me } = await api.get("/auth/me");
    const primary = me.memberships.find((m) => m.is_primary) || me.memberships[0];
    const type = primary ? primary.org_type : null;
    if (type === "INTERNAL") navigate("/control-centre");
    else if (type === "SUPPLIER") navigate("/supplier");
    else navigate("/client");
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();  // also invoked by InputOTP onComplete with the value, not an event
    setSubmitting(true);
    setError(null);
    try {
      await verifyOtp(email, code);
      toast.success("Signed in successfully");
      await goToWorkspace();
    } catch (err) {
      setError("Invalid or expired verification code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#F6F8FB] px-3 py-6 sm:px-4 sm:py-8">
      {/* faint arcs from the creative - CSS only, no raster */}
      <div aria-hidden className="pointer-events-none absolute -left-40 top-1/2 h-[760px] w-[760px] -translate-y-1/2 rounded-full border border-jigsaw-navy/[0.06]" />
      <div aria-hidden className="pointer-events-none absolute -right-48 top-1/2 h-[820px] w-[820px] -translate-y-1/2 rounded-full border border-jigsaw-orange/[0.10]" />
      <div aria-hidden className="pointer-events-none absolute left-[60px] top-[62px] hidden xl:block">
        <p className="text-[11px] font-semibold uppercase leading-5 tracking-[0.2em] text-jigsaw-navy/60">People<br />Places<br />Possibilities</p>
        <span className="mt-2 block h-0.5 w-10 bg-jigsaw-orange" />
      </div>
      <div aria-hidden className="pointer-events-none absolute bottom-[60px] right-[60px] hidden text-right xl:block">
        <p className="text-[11px] font-semibold uppercase leading-5 tracking-[0.2em] text-jigsaw-navy/60">A smarter way<br />to stay connected</p>
        <span className="mt-2 ml-auto block h-0.5 w-10 bg-jigsaw-orange" />
      </div>

      <div className="grid w-full min-w-0 max-w-[1066px] overflow-hidden rounded-2xl bg-white shadow-[0_12px_28px_-4px_rgba(16,24,47,0.14)] md:min-h-[736px] md:grid-cols-2">
        {/* ---------- left: form ---------- */}
        <div className="flex min-w-0 flex-col px-5 py-8 sm:px-8 sm:py-10 md:px-[56px] md:pb-10 md:pt-[44px]">
          <img data-testid="login-logo" src={LOGO_URL} alt="Jigsaw Conferences Ltd - Temporary Housing Solutions" className="w-full max-w-[420px] self-start object-contain" />
          <div className="mt-10 md:mt-[52px]">
            {mode === "password" ? (
              <>
              <PasswordSignIn email={email} setEmail={setEmail} onSignedIn={goToWorkspace} />
              <button data-testid="login-mode-otp" type="button" onClick={() => setMode("otp")} className="mt-6 text-sm text-jigsaw-navy underline-offset-4 hover:underline">Use a one-time code instead</button>
              </>
            ) : step === "email" ? (
              <>
              <form onSubmit={handleRequestOtp} className="space-y-6" noValidate>
                <div>
                  <h1 className="font-display text-[36px] font-normal leading-tight text-jigsaw-navy">Sign in to your account</h1>
                  <p className="mt-2 text-[23px] leading-snug text-jigsaw-muted">Access your workspace and manage temporary housing requests with confidence.</p>
                </div>
                <div className="space-y-2">
                  <label htmlFor="login-email" className="text-sm font-medium text-jigsaw-navy">E-mail address</label>
                  <p className="text-xs text-jigsaw-muted">Sign in with your email address and a 6-digit one-time code - no password needed.</p>
                  <Input id="login-email" data-testid="login-email-input" type="email" autoComplete="email" required autoFocus value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.co.uk" className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
                </div>
                {error && <p data-testid="login-error" role="alert" className="text-sm text-jigsaw-danger">{error}</p>}
                <Button data-testid="login-request-otp-button" type="submit" disabled={submitting || !email}
                  className="h-12 w-full rounded-lg bg-jigsaw-orange text-[15px] font-semibold text-white hover:bg-jigsaw-orangeDark disabled:bg-jigsaw-orange/40 disabled:text-white">
                  {submitting ? "Requesting code..." : "Continue"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="flex items-center gap-2 text-xs text-jigsaw-muted"><ShieldCheck className="h-4 w-4 text-jigsaw-orange" /> Secure one-time codes. Access is by invitation from Jigsaw Conferences.</p>
              </form>
              <button data-testid="login-mode-password" type="button" onClick={() => { setMode("password"); setError(null); }} className="mt-6 text-sm text-jigsaw-navy underline-offset-4 hover:underline">Sign in with a password instead</button>
              <MicrosoftSignIn />
              </>
            ) : (
              <form onSubmit={handleVerify} className="space-y-6" noValidate>
                <div>
                  <h1 className="font-display text-[36px] font-normal leading-tight text-jigsaw-navy">Enter your code</h1>
                  <p className="mt-2 text-[17px] leading-snug text-jigsaw-muted">{devOtp ? <>A 6-digit code has been issued for <span className="font-medium text-jigsaw-navy [overflow-wrap:anywhere]">{email}</span>. It expires in 10 minutes.</> : <>If <span className="break-all font-medium text-jigsaw-navy">{email}</span> belongs to a provisioned, active account, a 6-digit code has been sent. It expires in 10 minutes.</>}</p>
                </div>
                {devOtp && (
                  <div data-testid="dev-otp-banner" className="rounded-lg border border-jigsaw-warning/30 bg-jigsaw-warningBg px-4 py-3 text-sm text-jigsaw-warning">
                    <p>Development environment: no e-mail transport is configured, so the code is shown on screen: <span className="font-mono-ref font-semibold">{devOtp}</span>.</p>
                    <p className="mt-1" data-testid="dev-otp-eligibility-note">A code is shown for every address and does not by itself grant access — only accounts provisioned and activated by a Jigsaw Admin can sign in. If this address has not been set up, verification will be refused.</p>
                  </div>
                )}
                <InputOTP data-testid="login-otp-input" maxLength={6} value={code} onChange={setCode} onComplete={() => handleVerify()}>
                  <InputOTPGroup className="w-full gap-1.5 sm:w-auto sm:gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 min-w-0 flex-1 rounded-lg border border-jigsaw-border text-lg sm:h-12 sm:w-12 sm:flex-none" />)}
                  </InputOTPGroup>
                </InputOTP>
                {error && <p data-testid="login-error" role="alert" className="text-sm text-jigsaw-danger">{error}</p>}
                <Button data-testid="login-verify-otp-button" type="submit" disabled={submitting || code.length !== 6}
                  className="h-12 w-full rounded-lg bg-jigsaw-orange text-[15px] font-semibold text-white hover:bg-jigsaw-orangeDark disabled:bg-jigsaw-orange/40 disabled:text-white">
                  {submitting ? "Verifying..." : "Verify and sign in"}
                </Button>
                <button data-testid="login-back-button" type="button" onClick={() => { setStep("email"); setCode(""); setError(null); }} className="text-sm text-jigsaw-muted underline-offset-4 hover:underline">
                  Use a different e-mail
                </button>
              </form>
            )}
          </div>
          <p className="mt-auto pt-8 text-xs text-jigsaw-muted">Sessions end after 15 minutes of inactivity.</p>
        </div>

        {/* ---------- right: globe artwork recreated from the supplied creative (public/brand/artwork/PROVENANCE.md) ---------- */}
        <div data-testid="login-brand-panel" className="relative hidden overflow-hidden bg-jigsaw-navy p-12 text-white md:flex md:flex-col">
          <img src="/brand/artwork/globe-panel.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover object-right" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(22,33,62,0.55)_0%,rgba(22,33,62,0.15)_60%,rgba(22,33,62,0.35)_100%)]" />
          <div className="relative mt-[120px] max-w-[400px]">
            <h2 className="font-display text-[44px] font-normal leading-[1.15]">A smarter platform for temporary housing and emergency response.</h2>
            <span className="mt-6 block h-1 w-20 bg-jigsaw-orange" />
            <ul data-testid="login-benefits-list" className="mt-8 space-y-4 text-[16px] text-white/95">
              {["Rapid placement support", "Trusted accommodation network", "Smarter case coordination", "24/7 emergency response"].map((t) => (
                <li key={t} className="flex items-center gap-3"><span className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-jigsaw-orange" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
